/*
  Saint Drape quick-add popup.

  <sd-quick-add>  — ONE shared popup (bottom-right). open(url) fetches the slim quick-add
                    view (product.quick-add template), injects [data-quick-add], slides in.
                    Closes itself on a cartUpdate (Dawn publishes it after an add).
  <sd-size-picker> — one pill row per product option; resolves the variant from the chosen
                    combination (matrix in [data-qa-variants]), disables values no available
                    variant can satisfy, writes the id into the sibling Dawn <product-form>
                    and updates the price. "Buy now" adds to the cart, then -> /checkout.

  The add-to-cart itself is Dawn's <product-form> (assets/product-form.js), so cards, PDP
  and this popup all share one cart engine. This file only handles presentation + wiring.

  Openers: any element with [data-quick-add-open][data-product-url]. Delegated below.
*/
(function () {
  if (!customElements.get('sd-quick-add')) {
    customElements.define(
      'sd-quick-add',
      class extends HTMLElement {
        connectedCallback() {
          this.content = this.querySelector('[data-qa-content]');
          // Close on backdrop click or the close button.
          this.addEventListener('click', (e) => {
            if (e.target === this || e.target.closest('[data-qa-close]')) this.close();
          });
          this.onKey = (e) => {
            if (e.key === 'Escape') this.close();
          };
          // Dawn publishes cartUpdate after a successful add — close the popup then.
          if (window.subscribe && window.PUB_SUB_EVENTS) {
            subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
              if (this.classList.contains('is-open')) this.close();
            });
          }
        }

        // `edit` (optional) = { lineKey, quantity, variantId } -> the popup becomes an
        // "update this cart line" dialog instead of an add-to-cart one.
        async open(url, edit) {
          if (!url || !this.content) return;
          this.edit = edit || null;
          this.classList.add('is-open', 'is-loading');
          this.classList.toggle('is-edit', !!this.edit);
          document.addEventListener('keydown', this.onKey);
          try {
            const res = await fetch(url);
            const html = await res.text();
            const frag = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-add]');
            // No fragment = the response wasn't the quick-add view (usually a malformed
            // URL that dropped ?view=quick-add). Don't leave an empty panel open.
            if (!frag) throw new Error(`quick-add view missing in response for ${url}`);
            this.content.innerHTML = frag.outerHTML;
            if (this.edit) this.applyEditMode();
          } catch (e) {
            console.error('quick-add fetch failed', e);
            this.close();
          } finally {
            this.classList.remove('is-loading');
          }
        }

        /*
         * Same markup, different verb. The picker, the variant matrix and the pre-checked
         * values all come from the fetched view for free — the line's current variant is
         * passed as `?variant=` in the URL, so Liquid's selected_or_first_available_variant
         * already has the right options ticked.
         */
        applyEditMode() {
          // Buy now makes no sense while editing a line that's already in the bag.
          this.content.querySelector('[data-qa-buynow]')?.remove();

          const addBtn = this.content.querySelector('[data-qa-add]');
          if (addBtn) {
            addBtn.classList.remove('sd-btn--outline');
            addBtn.classList.add('sd-btn--primary');
            const label = addBtn.querySelector('.sd-btn__label');
            const loading = addBtn.querySelector('.sd-btn__loading');
            if (label) label.textContent = 'Update';
            if (loading) loading.textContent = 'Updating…';
          }

          // CAPTURE phase + stopImmediatePropagation: Dawn's <product-form> binds its own
          // bubble-phase submit handler to this same form, and it would post an ADD.
          this.content.querySelector('form')?.addEventListener('submit', this.onEditSubmit.bind(this), true);
        }

        async onEditSubmit(event) {
          event.preventDefault();
          event.stopImmediatePropagation();

          const variantId = this.content.querySelector('[data-qa-id]')?.value;
          if (!variantId) return;

          // Nothing changed — close quietly. Critically, swapping to the SAME variant would
          // merge the add into this very line and the follow-up remove would delete it.
          if (String(variantId) === String(this.edit.variantId)) return this.close();

          if (!window.SdCart) {
            console.error('quick-add edit: SdCart helper missing (is this the cart page?)');
            return;
          }

          const addBtn = this.content.querySelector('[data-qa-add]');
          addBtn?.classList.add('loading');
          if (addBtn) addBtn.disabled = true;

          try {
            await window.SdCart.swapLine({
              lineKey: this.edit.lineKey,
              variantId,
              quantity: this.edit.quantity,
            });
            this.close();
          } catch (e) {
            console.error('quick-add edit failed', e);
            addBtn?.classList.remove('loading');
            if (addBtn) addBtn.disabled = false;
          }
        }

        close() {
          this.classList.remove('is-open', 'is-edit');
          this.edit = null;
          document.removeEventListener('keydown', this.onKey);
          if (this.content) this.content.innerHTML = '';
        }
      }
    );
  }

  if (!customElements.get('sd-size-picker')) {
    customElements.define(
      'sd-size-picker',
      class extends HTMLElement {
        connectedCallback() {
          this.root = this.closest('[data-quick-add]') || document;
          this.idInput = this.root.querySelector('[data-qa-id]');
          this.priceEl = this.root.querySelector('[data-qa-price]');
          this.buyBtn = this.root.querySelector('[data-qa-buynow]');
          this.addBtn = this.root.querySelector('[data-qa-add]');
          this.buttons = Array.from(this.querySelectorAll('.sd-qa__size'));
          try {
            this.variants = JSON.parse(this.querySelector('[data-qa-variants]')?.textContent || '[]');
          } catch (e) {
            this.variants = [];
          }
          // One slot per option (position 1..n), seeded from the pre-checked values.
          this.optionCount = new Set(this.buttons.map((b) => b.dataset.option)).size;
          this.selection = new Array(this.optionCount).fill(null);
          this.buttons.forEach((b) => {
            if (b.getAttribute('aria-checked') === 'true') this.selection[this.pos(b)] = b.dataset.value;
            b.addEventListener('click', () => this.select(b));
          });
          // If a checkout app (PenguinCOD) already took this click — it listens first and
          // calls preventDefault — let it run its own buy-now flow instead of ours.
          this.buyBtn?.addEventListener('click', (e) => {
            if (e.defaultPrevented) return;
            this.buyNow();
          });
          this.refresh();
        }

        pos(btn) {
          return (parseInt(btn.dataset.option, 10) || 1) - 1;
        }

        select(btn) {
          if (btn.disabled) return;
          this.selection[this.pos(btn)] = btn.dataset.value;
          this.refresh();
        }

        // First variant matching every currently-chosen option value.
        find(selection) {
          return this.variants.find((v) => selection.every((val, i) => val != null && v.options[i] === val));
        }

        refresh() {
          this.buttons.forEach((b) => {
            const i = this.pos(b);
            b.setAttribute('aria-checked', this.selection[i] === b.dataset.value ? 'true' : 'false');
            // Selectable if SOME available variant has this value alongside the other
            // chosen options — the option's own current value must not constrain itself.
            const candidate = this.selection.slice();
            candidate[i] = b.dataset.value;
            b.disabled = !this.variants.some(
              (v) => v.available && candidate.every((val, j) => val == null || v.options[j] === val)
            );
          });

          const variant = this.find(this.selection);
          this.current = variant && variant.available ? variant.id : null;
          if (this.idInput && variant) this.idInput.value = variant.id;
          if (this.priceEl && variant) this.priceEl.textContent = variant.price;
          if (this.addBtn) this.addBtn.disabled = !this.current;
          if (this.buyBtn) this.buyBtn.setAttribute('aria-disabled', this.current ? 'false' : 'true');
        }

        // Buy now = the PDP's proven path: add to the REAL cart, confirm it worked, then
        // go to /checkout. Not the /cart/<id>:1 permalink: a permalink skips the cart, and
        // with the COD app intercepting checkout the shopper landed on an EMPTY cart.
        // The body is the popup's own add form (FormData), so anything an app injects
        // into it rides along exactly as it does for "Add to bag".
        async buyNow() {
          if (!this.current) return;
          this.buyBtn?.setAttribute('aria-disabled', 'true');
          const form = this.root.querySelector('form[action*="/cart/add"]');
          if (this.idInput) this.idInput.value = this.current;
          const body = form ? new FormData(form) : null;
          if (body) {
            body.set('id', this.current);
            if (!body.get('quantity')) body.set('quantity', '1');
          }
          try {
            const res = await fetch(`${(window.routes && routes.cart_add_url) || '/cart/add'}.js`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: body || JSON.stringify({ id: this.current, quantity: 1 }),
            });
            if (!res.ok) throw new Error(`add failed: ${res.status} ${await res.text()}`);
            window.location.href = '/checkout';
          } catch (e) {
            console.error('buy now failed', e);
            this.buyBtn?.setAttribute('aria-disabled', 'false');
          }
        }
      }
    );
  }

  // Delegated openers. [data-quick-add-open] adds a product; [data-qa-edit] (cart page)
  // opens the SAME popup pointed at an existing line, to change its variant.
  document.addEventListener('click', (e) => {
    const editor = e.target.closest('[data-qa-edit]');
    if (editor) {
      e.preventDefault();
      document.querySelector('sd-quick-add')?.open(editor.getAttribute('data-product-url'), {
        lineKey: editor.getAttribute('data-line-key'),
        quantity: parseInt(editor.getAttribute('data-quantity'), 10) || 1,
        variantId: editor.getAttribute('data-variant-id'),
      });
      return;
    }

    const opener = e.target.closest('[data-quick-add-open]');
    if (!opener) return;
    e.preventDefault();
    document.querySelector('sd-quick-add')?.open(opener.getAttribute('data-product-url'));
  });
})();
